import { Injectable, Injector } from '@angular/core';
import { Router } from '@angular/router';
import { PermissionsModuleAccessLevel } from '@predictiveindex/client-permissions-lib';
import { piIconOpenNewWindow } from '@predictiveindex/pi-client-icons';
import {
    IDropDownItem,
    IModule,
    TapestryIconRegistryService,
    TapestrySidenavService
} from '@predictiveindex/tapestry';
import {
    adminRoutePath,
    design2Home,
    designExplainerPath,
    diagnose2RoutePath,
    diagnoseExplainerPath,
    groupAnalyticsPath,
    Hire2Link,
    hireExplainerPath,
    Inspire2Link,
    inspireExplainerPath,
    NavOptions,
    performExplainerPath,
    performNoAccessPath,
    PI,
    RoleType
} from '../../constants-types';
import { ModuleName, SellerToolType } from '../../core-data-model';
import { SellerToolsPeekData } from '../../core-data-model/seller-tools/seller-tools-peek-panel-data.model';
import { AuthenticationService } from '../../core-services';
import { Util } from '../../core-utilities';
import {
    FeatureFlags,
    FeatureFlagsService,
    InitDataService,
    ModuleAccessService,
    ModuleUserAccessService,
    UserProfileSettingsService
} from '../../system-services';
import { TranslationService } from '../../translations';
import { UpgradeAccountService } from '../upgrade-account';
import {
    SELLER_TOOL_PEEK_DATA,
    SellerToolsBaseComponent
} from './seller-tools/seller-tools-base.component';
declare let pendo: any;

@Injectable({
    providedIn: 'root'
})
export class GlobalNavService {
    isPendoEnabled = PI.appSettings.pendoOn;
    availableModules: IModule[] = [];

    constructor(
        private auth: AuthenticationService,
        private featureFlagService: FeatureFlagsService,
        private moduleUserAccessService: ModuleUserAccessService,
        private moduleAccess: ModuleAccessService,
        public tapestrySidenavService: TapestrySidenavService,
        private router: Router,
        private TR: TranslationService,
        private util: Util,
        private initDataService: InitDataService,
        private userProfileSettingsService: UserProfileSettingsService,
        private upgradeAccountService: UpgradeAccountService,
        private tapestryIconRegistry: TapestryIconRegistryService
    ) {
        this.updateAvailableModulesForNavigation();
        this.tapestryIconRegistry.registerIcons([piIconOpenNewWindow]);
    }

    async menuAction(id: GlobalNavMenuActions): Promise<void> {
        switch (id) {
            case GlobalNavMenuActions.MobileDesign:
                this.getModuleNavigationDetails(
                    NavOptions.Design
                ).onClickAction();
                break;
            case GlobalNavMenuActions.MobileDiagnose:
                this.getModuleNavigationDetails(
                    NavOptions.Diagnose
                ).onClickAction();
                break;
            case GlobalNavMenuActions.MobileInspire:
                this.getModuleNavigationDetails(
                    NavOptions.Inspire
                ).onClickAction();
                break;
            case GlobalNavMenuActions.MobileHire:
                this.getModuleNavigationDetails(
                    NavOptions.Hire
                ).onClickAction();
                break;
            case GlobalNavMenuActions.MobilePerform:
                this.getModuleNavigationDetails(
                    NavOptions.Perform
                ).onClickAction();
                break;
            case GlobalNavMenuActions.PlansAndPricing:
                await this.upgradeAccountService.upgradeAccount(false);
                break;
            case GlobalNavMenuActions.Administration:
                this.router.navigateByUrl(`/${adminRoutePath}`);
                break;
            case GlobalNavMenuActions.MobileGroupAnalytics:
                this.router.navigateByUrl(`${groupAnalyticsPath}`);
                break;
            case GlobalNavMenuActions.Pendo:
                this.togglePendo();
                break;
            case GlobalNavMenuActions.Tapestry:
                this.launchTapestry();
                break;
            case GlobalNavMenuActions.TapestryPlayground:
                this.launchTapestryPlayground();
                break;
            case GlobalNavMenuActions.TranslationsNormal:
                this.TR.setTranslationMode('Normal');
                break;
            case GlobalNavMenuActions.TranslationsHash:
                this.TR.setTranslationMode('Hash');
                break;
            case GlobalNavMenuActions.TranslationsEnclose:
                this.TR.setTranslationMode('Enclose');
                break;
            case GlobalNavMenuActions.TranslationsKeys:
                this.TR.setTranslationMode('Key');
                break;
            case GlobalNavMenuActions.TranslationsEnglish:
                this.reloadEnglish();
                break;
            case GlobalNavMenuActions.TranslationsEnglishInstrumented:
                this.reloadEnglishInstrumented();
                break;
            case GlobalNavMenuActions.MobileHelp:
                this.showHelpPendo();
                break;
            case GlobalNavMenuActions.MobileSellerTools:
                this.openOIPeek();
                break;
            case GlobalNavMenuActions.MobileSignout:
                this.logout();
                break;
            default:
                break;
        }
    }

    getTranslationMenuItems(): IDropDownItem[] {
        return [
            {
                title: this.TR.key('translation.normalMode'),
                id: GlobalNavMenuActions.TranslationsNormal
            },
            {
                title: this.TR.key('translation.hashMode'),
                id: GlobalNavMenuActions.TranslationsKeys
            },
            {
                title: this.TR.key('translation.encloseMode'),
                id: GlobalNavMenuActions.TranslationsEnclose
            },
            {
                title: this.TR.key('translation.keyMode'),
                id: GlobalNavMenuActions.TranslationsKeys
            },
            {
                title: this.TR.key('translation.goToEnglish'),
                id: GlobalNavMenuActions.TranslationsEnglish
            },
            {
                title: this.TR.key('translation.goToEnglishInstrumented'),
                id: GlobalNavMenuActions.TranslationsEnglishInstrumented
            }
        ];
    }

    togglePendo(): void {
        document.cookie = `PI_OVERRIDE_PENDO_ENABLE=; expires=31 Dec ${
            this.isPendoEnabled ? '1700' : '2100'
        } 12:00:00 UTC; path=/`;
        window.location.reload();
    }

    getPendoKey(): string {
        return this.isPendoEnabled
            ? this.TR.key('globalnav.pendo.disable')
            : this.TR.key('globalnav.pendo.enable');
    }

    showHelpPendo() {
        if (PI.appSettings.pendoOn && (window as any).pendo !== undefined) {
            pendo.BuildingBlocks.BuildingBlockResourceCenter.getResourceCenter().show();
        }
    }

    launchTapestry(): void {
        window.open('https://tapestry.predictiveindex.com', '_blank');
    }

    launchTapestryPlayground(): void {
        this.router.navigateByUrl('tapestryPlayground');
    }

    reloadEnglish() {
        this.util.queryString.setAndReload([
            {
                key: 'languageLocale',
                value: 'en-US'
            }
        ]);
    }

    reloadEnglishInstrumented() {
        this.util.queryString.setAndReload([
            {
                key: 'languageLocale',
                value: 'en-ir'
            }
        ]);
    }

    isSubscriptionAndBillingEnabled() {
        return (
            this.featureFlagService.getByFlag(
                FeatureFlags.enableSubscriptionAndBilling
            ) &&
            this.moduleUserAccessService.hasAccessLevelForAnyModule(
                PermissionsModuleAccessLevel.AllModuleAdmin
            )
        );
    }

    openOIPeek() {
        this.tapestrySidenavService.open({
            component: SellerToolsBaseComponent,
            title: this.TR.key('client-core.global-nav.partner-support.title'),
            injector: Injector.create({
                providers: [
                    {
                        provide: SELLER_TOOL_PEEK_DATA,
                        useValue: <SellerToolsPeekData>{
                            sellerToolType: SellerToolType.OpenInvitation,
                            prospectData: []
                        }
                    }
                ]
            }),
            helpLink: {
                displayName: this.TR.key(
                    'client-core.global-nav.partner-support.help-link'
                ),
                url: 'https://predictiveindex.my.site.com/partners/'
            }
        });
    }

    logout() {
        this.auth.logout();
    }

    updateAvailableModulesForNavigation() {
        // This is the order that the modules will appear in the navigation
        this.availableModules = [
            NavOptions.Hire,
            NavOptions.Inspire,
            NavOptions.Design,
            NavOptions.Diagnose,
            NavOptions.Perform
        ]
            .filter((moduleName) => this.shouldDisplayModule(moduleName))
            .map((moduleName) => this.getModuleNavigationDetails(moduleName));
    }

    /**
     * Determine if module should show up in navigation based on feature flags.
     * When we are ready to show all modules always, this function can be removed
     * and the filter function in updateAvailableModulesForNavigation can be removed.
     *
     * The logic for this is all held within LaunchDarkly. Essentially, if we want
     * to show unlock pages then the default value should be set to true, if we only
     * want people with the activated module to have access then the default value
     * should be false.
     */
    shouldDisplayModule(moduleName: NavOptions): boolean {
        switch (moduleName) {
            case NavOptions.Hire:
                return true;
            case NavOptions.Design:
                return true;
            case NavOptions.Inspire:
                return (
                    this.hasAccessToInspire2() ||
                    this.isAnyModuleInternalActivationAvailable()
                );
            case NavOptions.Diagnose:
                return (
                    this.hasAccessToDiagnose2() ||
                    this.isAnyModuleInternalActivationAvailable()
                );
            // 2.0 Perform is always visible
            // See the complimentary logic for 1.0 in legacy-navigation-menu.component.ts
            case NavOptions.Perform:
                return true;
        }
    }

    getModuleNavigationDetails(moduleName: NavOptions): IModule {
        /* We're setting the initial isActiveModule property to false to avoid an issue,
           where the nav doesn't get updated when we navigate to a different nav menu item.
           Thus we will have two menu items that are hihglighted and that's confusing to the user.

           The active module highlight code is handled inside Tapestry's nav-menu-list.component,
           which uses the router to check and highlight the appropriate nav meneu item.
         */
        switch (moduleName) {
            case NavOptions.Hire:
                return {
                    moduleName: this.TR.key('label.common-terms.hire'),
                    onClickAction: () => {
                        this.router.navigateByUrl(
                            this.isThirdPartyUser() ||
                                (this.moduleAccess.hasHireHero &&
                                    this.hasUserAccessSpecifiedForModule(
                                        NavOptions.Hire
                                    ))
                                ? `${Hire2Link}`
                                : `${hireExplainerPath}`
                        );
                    },
                    trackingName: 'hire2MenuSectionHeader',
                    untranslatedModuleName: 'Hire'
                };
            case NavOptions.Design:
                return {
                    moduleName: this.TR.key('label.common-terms.design'),
                    onClickAction: () => {
                        this.router.navigateByUrl(
                            this.isThirdPartyUser() ||
                                (this.moduleAccess.hasDesign &&
                                    this.hasUserAccessSpecifiedForModule(
                                        NavOptions.Design
                                    ))
                                ? `/${design2Home}`
                                : `${designExplainerPath}`
                        );
                    },
                    trackingName: 'designMenuSectionHeader',
                    untranslatedModuleName: 'Design'
                };
            case NavOptions.Inspire:
                return {
                    moduleName: this.TR.key('label.common-terms.inspire'),
                    onClickAction: () => {
                        this.router.navigateByUrl(
                            this.isThirdPartyUser() ||
                                (this.moduleAccess.hasInspire2 &&
                                    this.hasUserAccessSpecifiedForModule(
                                        NavOptions.Inspire
                                    ))
                                ? `/${Inspire2Link}`
                                : `${inspireExplainerPath}`
                        );
                    },
                    trackingName: 'inspire2MenuSectionHeader',
                    untranslatedModuleName: 'Inspire'
                };
            case NavOptions.Diagnose:
                return {
                    moduleName: this.TR.key('label-common-terms.engage'),
                    onClickAction: () => {
                        this.router.navigateByUrl(
                            this.hasAccessToDiagnose2()
                                ? `/${diagnose2RoutePath}`
                                : `${diagnoseExplainerPath}`
                        );
                    },
                    trackingName: 'diagnoseMenuSectionHeader',
                    untranslatedModuleName: 'Diagnose'
                };
            case NavOptions.Perform:
                return {
                    moduleName: this.TR.key('label.common-terms.perform'),
                    onClickAction: () => {
                        if (this.moduleAccess.hasPerform) {
                            if (
                                this.featureFlagService.getByFlag(
                                    FeatureFlags.enablePerformOrgManagement
                                ) &&
                                !this.moduleUserAccessService.hasBasicPerformPermissions() &&
                                this.moduleAccess.isProductSelfServe(
                                    ModuleName.Perform
                                )
                            ) {
                                this.router.navigateByUrl(performNoAccessPath);
                            }
                            // Always go to the Perform app if they have access
                            else
                                window.open(
                                    `${PI.appSettings.performUrl}/token_auth/pi_auth0_login`,
                                    '_blank'
                                );
                        } else if (
                            this.featureFlagService.getByFlag(
                                FeatureFlags.enablePerformInternalActivation
                            )
                        ) {
                            // If the internal activation FF is on and current user is an admin, go to the explainer page.
                            if (!this.isPerformNavInteractionDisabled()) {
                                this.router.navigateByUrl(performExplainerPath);
                            } else {
                                return;
                            }
                        } else {
                            const performMarketingPageFullUrl =
                                PI.appSettings.performMarketingPageUrl +
                                (this.moduleAccess.hasV2ModuleAccess
                                    ? '?source=software-perform-nav-2'
                                    : '?source=software-perform-nav-1');
                            window.open(performMarketingPageFullUrl, '_blank');
                        }
                    },
                    trackingName: 'performMenuSectionHeader',
                    untranslatedModuleName: 'Perform',
                    // Only show the icon if it is an external link, which
                    // will be in all cases except when the user doesn't have
                    // Perform and the internal activation
                    // FF is on.
                    icon: this.showPerformIcon()
                        ? piIconOpenNewWindow.name
                        : null,
                    isInteractionDisabled:
                        this.isPerformNavInteractionDisabled(),
                    tooltipContent: this.isPerformNavInteractionDisabled()
                        ? this.TR.key('globalnav.perform-disabled.tooltip')
                        : ''
                };
        }
    }

    private showPerformIcon(): boolean {
        if (
            this.featureFlagService.getByFlag(
                FeatureFlags.enablePerformOrgManagement
            ) &&
            !this.moduleUserAccessService.hasBasicPerformPermissions() &&
            this.moduleAccess.isProductSelfServe(ModuleName.Perform)
        ) {
            return false;
        }

        return !(
            !this.moduleAccess.hasPerform &&
            this.featureFlagService.getByFlag(
                FeatureFlags.enablePerformInternalActivation
            )
        );
    }

    hasAccessToDiagnose2(): boolean {
        return (
            this.isThirdPartyUser() ||
            (this.moduleAccess.hasDiagnose2 &&
                this.hasUserAccessSpecifiedForModule(NavOptions.Diagnose))
        );
    }

    hasAccessToInspire2(): boolean {
        return (
            this.isThirdPartyUser() ||
            (this.moduleAccess.hasInspire2 &&
                this.hasUserAccessSpecifiedForModule(NavOptions.Inspire))
        );
    }

    isAnyModuleInternalActivationAvailable(): boolean {
        // Business requirement: only Admins or above, of 2.0 modules, should be able to internally activate any module
        return this.moduleUserAccessService.hasAccessLevelForAnyModule(
            PermissionsModuleAccessLevel.AllModuleAdmin
        );
    }

    isThirdPartyUser() {
        return !this.userProfileSettingsService.isUserLoggedInDefaultCompany();
    }

    /**
     * Returns whether or not user access was specified for module.
     * Returns false only when user access level is not specified `Unknown`.
     * Returns true on `No Access` and any other access above.
     */
    hasUserAccessSpecifiedForModule(module: NavOptions) {
        const map = this.initDataService.userAccessLevelMap;
        if (!map) {
            return false;
        }
        const access = map.get(module);
        return (
            !!access &&
            access.userAccessLevel >= PermissionsModuleAccessLevel.NoAccess
        );
    }

    /** Only disable the Perform nav item interaction when
     *  they don't have Perform module access and current user is not an admin
     */
    isPerformNavInteractionDisabled(): boolean {
        // Short circuit the flow for Classic Experience
        if (!this.moduleAccess.isProductSelfServe(ModuleName.Perform)) {
            return false;
        }

        return (
            this.featureFlagService.getByFlag(
                FeatureFlags.enablePerformInternalActivation
            ) &&
            !this.moduleAccess.hasPerform &&
            !this.initDataService.user.hasPermissionLevelOrGreater(
                RoleType.AccountAdmin
            )
        );
    }
}

export enum GlobalNavMenuActions {
    Administration = 'administration',
    Pendo = 'togglePendo',
    Tapestry = 'launchTapestry',
    TapestryPlayground = 'launchTapestryPlayground',
    TranslationsNormal = 'translation.normalMode',
    TranslationsHash = 'translation.hashMode',
    TranslationsEnclose = 'translation.encloseMode',
    TranslationsKeys = 'translation.keyMode',
    TranslationsEnglish = 'translation.goToEnglish',
    TranslationsEnglishInstrumented = 'translation.goToEnglishInstrumented',
    PlansAndPricing = 'plans-and-pricing',
    MobileDevMenu = 'developer-menu',
    MobileAdminMenu = 'admin',
    MobileGroupAnalytics = 'group-analytics',
    MobileProfile = 'profile',
    MobileHelp = 'help',
    MobileSignout = 'signout',
    MobileDesign = 'Design',
    MobileHire = 'Hire',
    MobileInspire = 'Inspire',
    MobileDiagnose = 'Diagnose',
    MobilePerform = 'Perform',
    MobileSellerTools = 'mobile-seller-tools'
}
